import { useRef, useState, type ComponentPropsWithRef } from "react"

import { Button, Dialog, DialogTrigger } from "@coval/ui"

import { isJsonObjectValue, type JsonObject } from "@/types/json"

import type {
  SimulationWrapperChangeHandler,
  SimulationWrapperConfig,
} from "../types"
import { ApiSetupDialog } from "./ApiSetupDialog"

interface ApiTestCaseCellProps {
  simulationMetadataInput: JsonObject | null | undefined
  onUpdate: SimulationWrapperChangeHandler<JsonObject>
  width: string
  disabled?: boolean
  cellProps?: ComponentPropsWithRef<"td">
  onDraftOpen?: () => () => void
}

export function ApiTestCaseCell({
  simulationMetadataInput,
  onUpdate,
  width,
  disabled = false,
  cellProps,
  onDraftOpen,
}: ApiTestCaseCellProps) {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const api = simulationMetadataInput?.api
  const hasUnsupportedConfig =
    api != null &&
    (!isJsonObjectValue(api) ||
      (api.pre_simulation !== undefined &&
        (!Array.isArray(api.pre_simulation) ||
          api.pre_simulation.some(
            (call) =>
              !isJsonObjectValue(call) ||
              typeof call.api_url !== "string" ||
              (call.method !== undefined && typeof call.method !== "string"),
          ))))
  const config = isJsonObjectValue(api)
    ? (api as unknown as SimulationWrapperConfig)
    : {}
  const firstCall = hasUnsupportedConfig
    ? undefined
    : config.pre_simulation?.[0]

  return (
    <td
      {...cellProps}
      className="px-2 py-3"
      style={{ width, verticalAlign: "top" }}
      onKeyDown={(event) => {
        const isTriggerNavigation =
          event.target === triggerRef.current &&
          [
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Home",
            "End",
            "PageUp",
            "PageDown",
          ].includes(event.key)
        if (event.target === event.currentTarget || isTriggerNavigation) {
          cellProps?.onKeyDown?.(event)
        }
      }}
    >
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            ref={triggerRef}
            type="button"
            tabIndex={cellProps ? -1 : undefined}
            variant="secondary"
            disabled={disabled || hasUnsupportedConfig}
            onClick={(event) => event.stopPropagation()}
          >
            {firstCall ? "Edit API Setup" : "Add API Setup"}
          </Button>
        </DialogTrigger>
        {hasUnsupportedConfig && (
          <p className="mt-1 text-xs text-coval-text-muted">
            This API setup uses an unsupported format. Edit it through the Test
            Cases API.
          </p>
        )}
        {firstCall && (
          <p className="mt-1 truncate text-xs text-coval-text-muted">
            {firstCall.method || "GET"} {firstCall.api_url}
          </p>
        )}
        {isOpen && (
          <ApiSetupDialog
            scope="test-case"
            config={config}
            disabled={disabled}
            onDraftOpen={onDraftOpen}
            onCancel={() => setIsOpen(false)}
            onApply={(updated) => {
              const applied = onUpdate({
                api: updated as unknown as JsonObject,
              })
              if (applied !== false) {
                setIsOpen(false)
              }
            }}
          />
        )}
      </Dialog>
    </td>
  )
}
